import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import PostCard from "@/components/PostCard";
import { Skel, Empty, ErrorState, useInView } from "@/components/common";
import { QuestionCard, CreatorCard } from "@/components/Cards";

const TABS = [["for-you", "For You"], ["following", "Following"], ["trending", "Trending"], ["festivals", "Festivals"], ["puja-ideas", "Puja Ideas"], ["decorations", "Decorations"], ["tips", "Devotional Tips"], ["womens", "Women's Corner"]];

export function PostFeed({ params, empty }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const key = JSON.stringify(params);
  const load = useCallback(async (pg) => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/posts", { params: { ...params, page: pg, limit: 12 } });
      setItems((it) => (pg === 1 ? r.data.items : [...it, ...r.data.items])); setMore(r.data.has_more); setPage(pg);
    } catch (e) { setErr(errMsg(e)); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => { setItems([]); load(1); }, [load]);
  const sentinel = useInView(() => { if (more && !loading && items.length) load(page + 1); }, [more, loading, page, items.length]);
  if (err && !items.length) return <ErrorState msg={err} retry={() => load(1)} />;
  if (!loading && !items.length) return empty || <Empty title="No posts here yet" sub="Be the first to share your puja corner." />;
  return (
    <>
      <div className="masonry columns-2 lg:columns-3 xl:columns-4">
        {items.map((p, i) => <PostCard key={p.id} post={p} tall={i % 3 === 1} onDeleted={(id) => setItems((x) => x.filter((y) => y.id !== id))} />)}
        {loading && [...Array(4)].map((_, i) => <Skel key={i} className="aspect-[4/5]" />)}
      </div>
      <div ref={sentinel} className="flex justify-center py-6">
        {more && !loading && <button onClick={() => load(page + 1)} className="btn-ghost" data-testid="feed-load-more">Load More</button>}
        {loading && items.length > 0 && <Loader2 className="h-5 w-5 animate-spin text-[#D97706]" />}
      </div>
    </>
  );
}

export default function Community() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "for-you";
  const { user, setSharePuja, requireAuth } = useApp();
  const [side, setSide] = useState(null);
  useEffect(() => { Promise.all([api.get("/creators"), api.get("/questions")]).then(([c, q]) => setSide({ c: c.data, q: q.data })); }, []);
  return (
    <div className="container-x py-8">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div><p className="eyebrow">Community</p><h1 className="font-display text-3xl font-bold text-[#6B1D2F] sm:text-4xl">Discover. Share. Connect.</h1></div>
        <div className="flex gap-2"><Link to="/community-chat" className="btn-ghost" data-testid="community-chat-link">Community Chat</Link><button onClick={requireAuth(() => setSharePuja(true))} className="btn-maroon" data-testid="community-share-btn">+ Share Your Puja</button></div>
      </div>
      <div className="no-scrollbar sticky top-16 z-30 -mx-4 mb-6 flex gap-2 overflow-x-auto bg-[#FFFDF7]/95 px-4 py-3 backdrop-blur" data-testid="community-tabs">
        {TABS.map(([v, l]) => <button key={v} onClick={() => setSp({ tab: v })} data-testid={`community-tab-${v}`} className={`chip !py-2 !text-sm ${tab === v ? "chip-on" : ""}`}>{l}</button>)}
      </div>
      <div className="grid gap-8 xl:grid-cols-[1fr_300px]">
        <div className="min-w-0"><PostFeed params={{ tab }} empty={tab === "following" && <Empty icon="🌸" title={user ? "Follow sakhis to fill this feed" : "Log in to see who you follow"} sub="Find creators in Trending and tap Follow." action={<button onClick={() => setSp({ tab: "trending" })} className="btn-maroon">See Trending</button>} />} /></div>
        <aside className="hidden space-y-6 xl:block">
          {side ? (<>
            <div className="card p-5"><p className="font-display mb-4 text-lg font-semibold">Trending Creators</p>
              <div className="space-y-3">{side.c.trending.slice(0, 5).map((u) => <Link key={u.id} to={`/profile/${u.id}`} className="flex items-center gap-3"><img src={u.avatar} alt="" className="h-10 w-10 rounded-full object-cover" /><div className="min-w-0"><p className="truncate text-sm font-semibold">{u.name}</p><p className="text-xs text-[#78716C]">{u.followers_count.toLocaleString("en-IN")} followers</p></div></Link>)}</div></div>
            <div className="card p-5"><p className="font-display mb-3 text-lg font-semibold">Most Helpful Members</p>
              <div className="flex flex-wrap gap-2">{side.c.helpful.map((u) => <Link key={u.id} to={`/profile/${u.id}`} className="chip">💝 {u.name}</Link>)}</div></div>
            <QuestionCard q={side.q[0]} />
          </>) : <Skel className="h-96" />}
        </aside>
      </div>
    </div>
  );
}

export function Creators() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/creators").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="container-x py-10"><Skel className="h-80" /></div>;
  return (
    <div className="container-x py-10 space-y-12">
      <div><p className="eyebrow">Gamification</p><h1 className="font-display text-4xl font-bold text-[#6B1D2F]">Trending Creators</h1>
        <div className="no-scrollbar mt-6 flex gap-4 overflow-x-auto pb-2">{d.trending.map((c) => <CreatorCard key={c.id} c={c} />)}</div></div>
      <div><h2 className="h2">Most Loved Puja Ideas</h2><div className="masonry mt-6 columns-2 lg:columns-3">{d.loved.map((p) => <PostCard key={p.id} post={p} />)}</div></div>
      <div><h2 className="h2">Most Helpful Members</h2><div className="no-scrollbar mt-6 flex gap-4 overflow-x-auto pb-2">{d.helpful.map((c) => <CreatorCard key={c.id} c={c} />)}</div></div>
    </div>
  );
}
